// The realm's sky over the overworld chart (docs/24 K5, K6): what the week's prevailing weather
// (`world_clock.weather`, rolled by the module from the climate) and the season look like at
// chart scale. Pure: the World screen's `WorldWeather` draws it. Cloud shadows, fog banks and
// tints lie on the land under the place nodes; rain, snow, sand and lightning fall over the
// chart. Rain and snow arrive in fronts that drift across the valley, so the map changes while
// you look at it, and next week the sky is a different one. The season sets the grade (a cold
// blue winter, an amber autumn) and, in cold country, freezes the river. Nothing here may make a
// node label hard to read: grades go on the terrain canvas only, never on the nodes.
import type { SeasonKey } from '../../../core/story/calendar';
import { climateFor, dayLight, type Phase, type RGB } from '../../../game/lighting/daylight';
import { cellHash } from '../../../game/tiling/hash';

export type OwWeather = 'clear' | 'overcast' | 'rain' | 'storm' | 'fog' | 'snow' | 'heat' | 'sandstorm' | 'blizzard' | 'frost' | 'sleet';
export const OW_WEATHERS: readonly OwWeather[] = ['clear', 'overcast', 'rain', 'storm', 'fog', 'snow', 'heat', 'sandstorm', 'blizzard', 'frost', 'sleet'];
export const parseOwWeather = (s: string | undefined): OwWeather | undefined => {
  const k = (s ?? '').split(':')[0]!.trim().toLowerCase();
  return (OW_WEATHERS as readonly string[]).includes(k) ? (k as OwWeather) : undefined;
};

/** The season's own dressing over the chart (docs/23 Y8), when the weather leaves room for it. */
export type SeasonDressing = 'petals' | 'motes' | 'leaves' | 'snow' | 'sand' | 'shimmer' | 'stars' | 'rain' | 'spores' | 'glow' | 'embers' | 'ash';
export type Precip = 'rain' | 'snow' | 'sleet' | 'sand';

export interface WorldSky {
  weather: OwWeather | undefined;
  /** Cloud shadows drifting over the land: how many, and how dark (alpha). */
  clouds: number;
  cloudAlpha: number;
  /** What falls, how much, and whether it comes in passing fronts (else everywhere). */
  precip: Precip | undefined;
  precipCount: number;
  fronts: boolean;
  /** Sideways drift of what falls, -1..1 (a gale is near 1). */
  wind: number;
  /** Fog banks on the land, 0..1. */
  fog: number;
  lightning: boolean;
  heat: boolean;
  /** Rime creeping in from the chart's edges. */
  frost: boolean;
  /** The river frozen over, 0..1 (cold country in winter, any hard frost). */
  ice: number;
  /** CSS filter for the terrain canvas: season grade, then the weather's light. */
  grade: string;
  /** A colour laid over the land (soft-light), or none. */
  tint: string | undefined;
  /** The season's particles, when the weather leaves room for them. */
  dressing: SeasonDressing | undefined;
}

/** The season's dressing by country (docs/23 Y8, unchanged). */
export function seasonDressing(season: SeasonKey, biome: string | undefined): SeasonDressing {
  if (biome === 'desert') return season === 'summer' ? 'shimmer' : season === 'autumn' ? 'sand' : season === 'spring' ? 'motes' : 'stars';
  if (biome === 'jungle') return season === 'summer' ? 'rain' : 'motes';
  // docs/23 Y9: under the rock, glow-worms in the seep and the bloom, spores in the spore-fall, a still dark motes in winter
  if (biome === 'underdark') return season === 'autumn' ? 'spores' : season === 'winter' ? 'motes' : 'glow';
  // docs/23 Y10: embers rise off the lava in the waking and the burning, ash drifts down in the ashfall and the ash winter
  if (biome === 'ashlands') return season === 'autumn' || season === 'winter' ? 'ash' : 'embers';
  return season === 'winter' ? 'snow' : season === 'autumn' ? 'leaves' : season === 'spring' ? 'petals' : 'motes';
}

/** Cold country, where winter freezes the water and greys the land toward blue. */
const coldCountry = (biome: string | undefined) => biome === 'boreal' || biome === 'highland' || biome === 'temperate' || biome === 'wetland';

const SEASON_GRADE: Record<SeasonKey, string> = {
  spring: 'saturate(1.06)',
  summer: 'saturate(1.08) brightness(1.03)',
  autumn: 'sepia(0.14) saturate(1.1)',
  winter: 'saturate(0.82) brightness(1.04) hue-rotate(-6deg)',
};

/** The weather's light at chart scale; mild, so the map stays a map. */
const WEATHER_GRADE: Record<OwWeather, string> = {
  clear: '',
  overcast: 'brightness(0.94) saturate(0.86)',
  rain: 'brightness(0.9) saturate(0.82)',
  storm: 'brightness(0.8) saturate(0.72) contrast(1.05)',
  fog: 'brightness(1.02) saturate(0.7) contrast(0.88)',
  snow: 'brightness(1.04) saturate(0.78)',
  heat: 'sepia(0.1) saturate(1.12) brightness(1.06)',
  sandstorm: 'sepia(0.38) saturate(0.9) brightness(0.92)',
  blizzard: 'brightness(1.06) saturate(0.55) contrast(0.85)',
  frost: 'saturate(0.8) brightness(1.05) hue-rotate(-8deg)',
  sleet: 'brightness(0.9) saturate(0.76)',
};

/** The seasons under the rock (docs/23 Y9): the seep wet and blue, the bloom glowing, the spore-fall hazed, the deep still drained. */
const DEEP_GRADE: Record<SeasonKey, string> = {
  spring: 'saturate(0.92) hue-rotate(4deg)',
  summer: 'saturate(1.1) brightness(1.04)',
  autumn: 'sepia(0.1) saturate(0.86)',
  winter: 'saturate(0.74) hue-rotate(-6deg)',
};
const DEEP_TINT: Record<SeasonKey, string> = {
  spring: 'rgba(80, 140, 170, 0.14)',
  summer: 'rgba(70, 210, 180, 0.12)',
  autumn: 'rgba(160, 180, 110, 0.14)',
  winter: 'rgba(130, 160, 230, 0.18)',
};

/** The Ashlands' seasons (docs/23 Y10): the waking smoky, the burning hot and orange, the ashfall grey and dark, the ash winter cold and drained. */
const ASH_GRADE: Record<SeasonKey, string> = {
  spring: 'saturate(0.9) sepia(0.08)',
  summer: 'saturate(1.08) sepia(0.12) brightness(1.02)',
  autumn: 'saturate(0.7) contrast(0.94)',
  winter: 'saturate(0.62) hue-rotate(-6deg)',
};
const ASH_TINT: Record<SeasonKey, string> = {
  spring: 'rgba(180, 120, 90, 0.14)',
  summer: 'rgba(255, 120, 50, 0.16)',
  autumn: 'rgba(90, 84, 82, 0.22)',
  winter: 'rgba(120, 130, 160, 0.2)',
};

/** The darkest any weather may make the land (the brightness factor in the grade). */
export const GRADE_FLOOR = 0.78;

export function worldSky(season: SeasonKey | undefined, biome: string | undefined, weather: OwWeather | undefined): WorldSky {
  const w = weather;
  if (biome === 'underdark') return deepSky(season, w);
  if (biome === 'ashlands') return ashSky(season, w);
  const cloudy: Partial<Record<OwWeather, [number, number]>> = {
    overcast: [7, 0.22], rain: [8, 0.26], storm: [9, 0.3], sleet: [8, 0.24], snow: [6, 0.16], fog: [3, 0.1], blizzard: [7, 0.14], sandstorm: [5, 0.16],
  };
  const fair = !w || w === 'clear' || w === 'heat' || w === 'frost';
  const [clouds, cloudAlpha] = w ? (cloudy[w] ?? (fair ? [3, 0.1] : [0, 0])) : [0, 0];
  const precip: Precip | undefined = w === 'rain' || w === 'storm' ? 'rain' : w === 'snow' || w === 'blizzard' ? 'snow' : w === 'sleet' ? 'sleet' : w === 'sandstorm' ? 'sand' : undefined;
  const precipCount = !precip ? 0 : w === 'storm' ? 130 : w === 'blizzard' ? 160 : w === 'sandstorm' ? 120 : w === 'snow' ? 130 : 110;
  // showers and snow come in fronts; a storm, a blizzard or a sandstorm is everywhere
  const fronts = w === 'rain' || w === 'snow' || w === 'sleet';
  const wind = w === 'blizzard' || w === 'sandstorm' ? 1 : w === 'storm' ? 0.6 : w === 'sleet' ? 0.35 : w === 'rain' ? 0.25 : w === 'snow' ? 0.15 : 0;
  const fog = w === 'fog' ? 1 : w === 'blizzard' ? 0.5 : w === 'sandstorm' ? 0.45 : w === 'rain' || w === 'sleet' ? 0.15 : 0;
  const winter = season === 'winter';
  const ice = w === 'blizzard' || (winter && (biome === 'boreal' || biome === 'highland')) ? 1
    : winter && coldCountry(biome) && (w === 'snow' || w === 'frost' || w === 'clear' || w === 'overcast' || w === 'fog') ? 0.85
    : w === 'frost' && biome !== 'jungle' ? 0.5 : 0;
  const grade = [season ? SEASON_GRADE[season] : '', w ? WEATHER_GRADE[w] : ''].filter(Boolean).join(' ');
  const tint = winter && coldCountry(biome) ? 'rgba(150, 186, 240, 0.22)'
    : w === 'frost' ? 'rgba(170, 200, 245, 0.18)'
    : season === 'autumn' && biome !== 'desert' && biome !== 'jungle' ? 'rgba(230, 150, 70, 0.12)'
    : w === 'sandstorm' ? 'rgba(214, 150, 80, 0.3)'
    : w === 'storm' ? 'rgba(40, 50, 80, 0.18)'
    : w === 'heat' || (season === 'summer' && biome === 'desert') ? 'rgba(255, 190, 110, 0.14)'
    : undefined;
  // the weather wins over the season's dressing (as `weathered` does in a fight): rain, snow and
  // sand ground the petals and pollen; a gale clears the air
  const base = season ? seasonDressing(season, biome) : undefined;
  const dressing = !base ? undefined
    : precip || w === 'fog' ? undefined
    : base === 'snow' && w !== 'overcast' && w !== 'frost' ? undefined // winter flakes only under a grey sky; clear frost has glints, not snow
    : base === 'shimmer' && w !== 'heat' && w !== 'clear' ? undefined
    : base;
  return {
    weather: w, clouds, cloudAlpha, precip, precipCount, fronts, wind, fog,
    lightning: w === 'storm', heat: w === 'heat' || (base === 'shimmer' && w === 'clear'), frost: w === 'frost' || (winter && coldCountry(biome) && w === 'clear'),
    ice, grade, tint, dressing,
  };
}

/**
 * The Underdark's sky (docs/23 Y9), which is no sky: no cloud passes and no wind blows; the seep
 * drips thinly everywhere at once, the fog is a spore haze, frost a rime from the shafts, and the
 * black water never freezes.
 */
function deepSky(season: SeasonKey | undefined, w: OwWeather | undefined): WorldSky {
  const seep = w === 'rain';
  const base = season ? seasonDressing(season, 'underdark') : undefined;
  return {
    weather: w, clouds: 0, cloudAlpha: 0,
    precip: seep ? 'rain' : undefined, precipCount: seep ? 40 : 0, fronts: false, wind: 0,
    fog: w === 'fog' ? 1 : w === 'overcast' ? 0.25 : 0,
    lightning: false, heat: w === 'heat', frost: w === 'frost', ice: 0,
    grade: [season ? DEEP_GRADE[season] : '', w ? WEATHER_GRADE[w] : ''].filter(Boolean).join(' '),
    tint: season ? DEEP_TINT[season] : undefined,
    dressing: seep || w === 'fog' ? undefined : base,
  };
}

/**
 * The Ashlands' sky (docs/23 Y10): the pall drifts over in heavy shadows, rain falls black, a
 * sandstorm is an ash storm (grey grit blowing across), the fog a sulphur haze; the lava never
 * freezes, and embers or ash fill the air whatever the season.
 */
function ashSky(season: SeasonKey | undefined, w: OwWeather | undefined): WorldSky {
  const cloudy: Partial<Record<OwWeather, [number, number]>> = { overcast: [9, 0.28], rain: [8, 0.28], storm: [9, 0.3], fog: [3, 0.1], sandstorm: [6, 0.2] };
  const [clouds, cloudAlpha] = w ? (cloudy[w] ?? [4, 0.14]) : [0, 0];
  const precip: Precip | undefined = w === 'rain' || w === 'storm' ? 'rain' : w === 'sandstorm' ? 'sand' : undefined;
  const base = season ? seasonDressing(season, 'ashlands') : undefined;
  return {
    weather: w, clouds, cloudAlpha,
    precip, precipCount: !precip ? 0 : w === 'storm' ? 130 : w === 'sandstorm' ? 120 : 100,
    fronts: w === 'rain',
    wind: w === 'sandstorm' ? 1 : w === 'storm' ? 0.6 : w === 'rain' ? 0.25 : 0,
    fog: w === 'fog' ? 1 : w === 'sandstorm' ? 0.5 : w === 'overcast' ? 0.15 : 0,
    lightning: w === 'storm', heat: w === 'heat', frost: w === 'frost', ice: 0,
    grade: [season ? ASH_GRADE[season] : '', w && w !== 'sandstorm' ? WEATHER_GRADE[w] : w === 'sandstorm' ? 'saturate(0.6) brightness(0.86)' : ''].filter(Boolean).join(' '),
    tint: w === 'sandstorm' ? 'rgba(70, 64, 62, 0.3)' : season ? ASH_TINT[season] : undefined,
    dressing: precip ? undefined : base,
  };
}

/**
 * The hours the chart is lit at: low suns and long shadows (dawn, golden hour, dusk), a bright
 * morning and afternoon, and one moonlit night. The week picks one (`owHour`), so the valley holds
 * its light all week and wakes to a new one with the next week's weather.
 */
export const OW_HOURS: readonly number[] = [6.6, 7.4, 9.5, 16.2, 17.4, 18.3, 19.2, 21.8];
/** The week's hour over the chart: a stable hash of the World and the week (every player sees one sky). */
export function owHour(worldId: string, weekIndex: bigint | number | undefined): number {
  return OW_HOURS[cellHash(`ow:${worldId}`, Number(weekIndex ?? 0n) % 100000, 0, 'hour') % OW_HOURS.length]!;
}

/** How much of the sun the week's weather lets through (1 a clear sky, near 0 under a storm). */
const SUN_THROUGH: Record<OwWeather, number> = {
  clear: 1, heat: 1, frost: 0.95, overcast: 0.35, rain: 0.25, storm: 0.15, fog: 0.2, snow: 0.4, sandstorm: 0.3, blizzard: 0.15, sleet: 0.25,
};

/** The chart's light (docs/19 at chart scale): what the CSS light layers and the canvas shadows draw. */
export interface OwLight {
  hour: number;
  phase: Phase;
  /** Multiply colour over the land (the floors of docs/19 N2 applied). */
  ambient: string;
  /** CSS angle the light travels (from the lit side toward the shaded one). */
  angle: number;
  /** The sun's (or moon's) colour on the lit side, and how strong it shows there (0..1). */
  sun: string; sunAlpha: number;
  /** The cold dark pooling on the far side, 0..1. */
  shadeAlpha: number;
  /** Where the sun stands on the chart's rim, as % of the board (its glow sits there). */
  glow: { x: number; y: number };
  /** Props cast hard pixel shadows: offset in atlas px (24 px cells) and darkness. */
  cast: { dx: number; dy: number; alpha: number };
  /** Lamps lit in the places (towns, keeps), 0..1. */
  lamps: number;
}

const css = (c: RGB, k = 255) => `rgb(${Math.round(c[0] * k)}, ${Math.round(c[1] * k)}, ${Math.round(c[2] * k)})`;

/** The light over the chart at `hour` in country `kit` under `weather`. Pure. */
export function owLight(hour: number, kit: string, weather: OwWeather | undefined): OwLight {
  const d = dayLight(hour, climateFor(kit));
  if (!d.sun) return glowChart(d);
  const sun = d.sun;
  const through = weather ? SUN_THROUGH[weather] : 1;
  const low = Math.min(1, Math.max(0, (sun.length - 0.45) / 2.15));   // 0 at noon, 1 at a setting sun
  // the light travels along the shadows: CSS angles run clockwise from up
  const angle = Math.round((Math.atan2(sun.dx, -sun.dy) * 180) / Math.PI);
  const reach = 6 * Math.min(2.6, sun.length) + 3;
  return {
    hour: d.hour, phase: d.phase,
    // a grey sky flattens the hour's colour toward white light
    ambient: css([1 - (1 - d.ambient[0]) * (0.55 + 0.45 * through), 1 - (1 - d.ambient[1]) * (0.55 + 0.45 * through), 1 - (1 - d.ambient[2]) * (0.55 + 0.45 * through)]),
    angle,
    sun: sun.moon ? 'rgb(150, 180, 255)' : css(d.sky),
    sunAlpha: (sun.moon ? 0.2 : 0.22 + 0.2 * low) * through,
    shadeAlpha: (0.22 + 0.28 * low + (sun.moon ? 0.12 : 0)) * (0.4 + 0.6 * through),
    glow: { x: Math.round(50 - 50 * sun.dx), y: Math.round(50 - 50 * sun.dy) },
    cast: { dx: Math.round(sun.dx * reach), dy: Math.round(sun.dy * reach), alpha: Math.min(0.5, sun.strength * 1.25) * (0.25 + 0.75 * through) },
    lamps: Math.max(0, Math.min(1, (d.torch - 0.7) * 4)) || (through < 0.3 ? 0.35 : 0),
  };
}

/**
 * The chart under the rock (docs/23 Y9): the glow lights it from everywhere, so no side is lit and nothing
 * casts a shadow; the lamps burn always. No sun washes it (the screen-blended sun layer only ever lightens,
 * which read as a milky haze), and the multiply is half-neutral so the floor sits dark and the art's own
 * glowing fungi and lamps carry the colour.
 */
function glowChart(d: ReturnType<typeof dayLight>): OwLight {
  const grey = 0.2126 * d.ambient[0] + 0.7152 * d.ambient[1] + 0.0722 * d.ambient[2];
  return {
    hour: d.hour, phase: d.phase,
    ambient: css([(d.ambient[0] + grey) / 2, (d.ambient[1] + grey) / 2, (d.ambient[2] + grey) / 2]),
    angle: 180,
    sun: css(d.sky), sunAlpha: 0,
    shadeAlpha: 0.18,
    glow: { x: 50, y: 50 },
    cast: { dx: 0, dy: 0, alpha: 0 },
    lamps: Math.max(0.6, Math.min(1, (d.torch - 0.7) * 4)),
  };
}

/** The lowest brightness factor in a grade string (1 when there is none), for the floor test. */
export function gradeBrightness(grade: string): number {
  let b = 1;
  for (const m of grade.matchAll(/brightness\(([\d.]+)\)/g)) b *= Number(m[1]);
  return b;
}
